/**
 * The app's loaders, as something that can be counted and watched. Backed by
 * the first-load gate store in production; the spec drives it by hand.
 */
export type LoaderActivity = {
  getActiveLoaderCount: () => number;
  /** Notifies on every change to the count. Returns the unsubscribe. */
  subscribe: (listener: () => void) => () => void;
};

type PageLoadTrackerDeps = {
  loaders: LoaderActivity;
  now: () => number;
  /**
   * How long the count has to stay at zero before the page counts as settled.
   * A page hands off between loaders in stages — the page-level loader clears
   * before the charts mount, and a late provider can spin up a frame or two
   * after its neighbours finished — so a single zero reading is not enough.
   */
  quietPeriodMs: number;
};

/**
 * Times a page from navigation until its last loader has cleared — the moment
 * the charts are actually on screen, later than their responses arriving.
 *
 * The duration runs up to when the count LAST hit zero, not to the end of the
 * quiet window that confirms it; the window is a measurement artefact.
 */
export const createPageLoadTracker = ({
  loaders,
  now,
  quietPeriodMs,
}: PageLoadTrackerDeps) => {
  let unsubscribe: (() => void) | null = null;
  let quietTimer: ReturnType<typeof setTimeout> | null = null;

  const clearQuietTimer = () => {
    if (quietTimer === null) return;
    clearTimeout(quietTimer);
    quietTimer = null;
  };

  const stop = () => {
    unsubscribe?.();
    unsubscribe = null;
    clearQuietTimer();
  };

  const start = (
    startedAt: number,
    onSettled: (durationMs: number) => void
  ) => {
    stop();

    const check = () => {
      if (loaders.getActiveLoaderCount() > 0) {
        clearQuietTimer();
        return;
      }
      // Already counting down from an earlier zero reading.
      if (quietTimer !== null) return;

      const settledAt = now();
      quietTimer = setTimeout(() => {
        quietTimer = null;
        stop();
        onSettled(settledAt - startedAt);
      }, quietPeriodMs);
    };

    unsubscribe = loaders.subscribe(check);
    check();
  };

  return { start, stop };
};
